import { useCallback, useState } from 'react';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { useDownloadUrlsMutation } from '../../../api';
import { fetchFilesFromUrls, getMimeTypeFromFilename, isMobilePlatform } from '../utils/downloadHelpers';

const createOutcome = (requestedIds, successfulIds = [], status) => {
  const successfulSet = new Set(successfulIds);
  const completedIds = requestedIds.filter(id => successfulSet.has(id));
  const failedIds = requestedIds.filter(id => !successfulSet.has(id));

  return {
    requestedCount: requestedIds.length,
    succeededCount: completedIds.length,
    failedCount: failedIds.length,
    successfulIds: completedIds,
    failedIds,
    status: status || (failedIds.length === 0 ? 'complete' : completedIds.length > 0 ? 'partial' : 'failed'),
  };
};

const isShareCancellation = (error) => {
  const name = error?.name;
  const message = String(error?.message || '').toLowerCase();
  const isTechnicalError = ['too large', 'exceeded', 'limit', 'size'].some(term => message.includes(term));
  return ['AbortError', 'SecurityError', 'NotAllowedError'].includes(name) && !isTechnicalError;
};

const getUniqueArchiveFilename = (filename, usedNames) => {
  const normalizedName = String(filename || 'download');
  const dotIndex = normalizedName.lastIndexOf('.');
  const baseName = dotIndex > 0 ? normalizedName.slice(0, dotIndex) : normalizedName;
  const extension = dotIndex > 0 ? normalizedName.slice(dotIndex) : '';
  let candidate = normalizedName;
  let suffix = 2;

  while (usedNames.has(candidate.toLowerCase())) {
    candidate = `${baseName} (${suffix})${extension}`;
    suffix += 1;
  }
  usedNames.add(candidate.toLowerCase());
  return candidate;
};

export const usePhotoDownload = (selectedPhotos, onDownloadComplete, onDownloadStart, onDownloadEnd, onDownloadProgress) => {
  const downloadUrlsMutation = useDownloadUrlsMutation();
  const [downloadState, setDownloadState] = useState({
    isDownloading: false,
    current: 0,
    total: 0,
    stage: 'idle',
    outcome: null,
  });

  const clearDownloadOutcome = useCallback(() => {
    setDownloadState(prev => ({ ...prev, outcome: null }));
  }, []);

  const downloadSelectedPhotos = useCallback(async () => {
    const requestedIds = Array.from(new Set(selectedPhotos || []));
    if (requestedIds.length === 0) return;

    let finalOutcome = null;
    setDownloadState({
      isDownloading: true,
      current: 0,
      total: requestedIds.length,
      stage: 'preparing',
      outcome: null,
    });

    try {
      onDownloadStart?.();
      onDownloadProgress?.(0, requestedIds.length, 'preparing');
      const response = await downloadUrlsMutation.mutateAsync({ fileNames: requestedIds });
      const urls = response?.urls;

      if (!Array.isArray(urls)) {
        throw new Error('Invalid download URL response');
      }

      const entriesById = new Map(urls.map(entry => [entry?.fileName, entry]));
      const validUrls = [];
      const failedIds = new Set();
      requestedIds.forEach((id) => {
        const entry = entriesById.get(id);
        if (entry?.url && !entry.error) {
          validUrls.push(entry);
        } else {
          failedIds.add(id);
        }
      });

      if (validUrls.length === 0) {
        finalOutcome = createOutcome(requestedIds);
        return;
      }

      setDownloadState(prev => ({ ...prev, stage: 'downloading', current: 0, total: validUrls.length }));
      onDownloadProgress?.(0, validUrls.length, 'downloading');
      const files = await fetchFilesFromUrls(validUrls, {
        concurrency: 6,
        onProgress: (current, total) => {
          setDownloadState(prev => ({ ...prev, current, total }));
          onDownloadProgress?.(current, total, 'downloading');
        },
        onFailure: id => failedIds.add(id),
      });

      const fetchedIds = new Set(files.map(file => file.originalFileName).filter(Boolean));
      validUrls.forEach(({ fileName }) => {
        if (!fetchedIds.has(fileName)) failedIds.add(fileName);
      });
      const handoffIds = Array.from(fetchedIds).filter(id => !failedIds.has(id));

      if (files.length === 0) {
        finalOutcome = createOutcome(requestedIds);
        return;
      }

      const canNativeShareFiles = isMobilePlatform()
        && typeof navigator !== 'undefined'
        && typeof navigator.canShare === 'function'
        && typeof navigator.share === 'function';
      const totalSize = files.reduce((sum, { blob }) => sum + (blob.size || 0), 0);

      if (canNativeShareFiles && totalSize <= 40 * 1024 * 1024) {
        try {
          const shareableFiles = files.map(({ filename, blob, contentType }) => {
            let safeFilename = filename;
            if (!safeFilename.includes('.')) {
              const detectedType = contentType || blob.type || '';
              if (detectedType.startsWith('image/')) safeFilename = `${safeFilename}.jpg`;
              if (detectedType.startsWith('video/')) safeFilename = `${safeFilename}.mp4`;
            }
            const mimeType = contentType || getMimeTypeFromFilename(safeFilename) || blob.type || 'application/octet-stream';
            return new File([blob], safeFilename, { type: mimeType });
          });

          if (navigator.canShare({ files: shareableFiles })) {
            try {
              await navigator.share({
                title: files.length === 1 ? 'Wedding Photo' : 'Wedding Photos',
                text: files.length === 1 ? 'Wedding photo' : `Sharing ${files.length} wedding photos`,
                files: shareableFiles,
              });
              finalOutcome = createOutcome(requestedIds, handoffIds);
              onDownloadComplete?.(finalOutcome);
              return;
            } catch (error) {
              if (isShareCancellation(error)) {
                finalOutcome = createOutcome(requestedIds, [], 'cancelled');
                return;
              }
            }
          }
        } catch (error) {
          console.warn('Error preparing files for Web Share API:', error);
        }
      }

      if (files.length === 1) {
        saveAs(files[0].blob, files[0].filename);
      } else {
        setDownloadState(prev => ({ ...prev, stage: 'packaging' }));
        onDownloadProgress?.(files.length, files.length, 'packaging');
        const zip = new JSZip();
        const usedNames = new Set();
        files.forEach(({ filename, blob }) => zip.file(getUniqueArchiveFilename(filename, usedNames), blob));
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        saveAs(zipBlob, `wedding-photos-${new Date().toISOString().slice(0, 10)}.zip`);
      }

      setDownloadState(prev => ({ ...prev, stage: 'complete' }));
      finalOutcome = createOutcome(requestedIds, handoffIds);
      onDownloadComplete?.(finalOutcome);
    } catch (error) {
      console.error('Download error:', error);
      finalOutcome = createOutcome(requestedIds);
    } finally {
      const outcome = finalOutcome || createOutcome(requestedIds);
      setDownloadState({ isDownloading: false, current: 0, total: 0, stage: 'idle', outcome });
      onDownloadEnd?.(outcome);
    }
  }, [selectedPhotos, onDownloadComplete, onDownloadStart, onDownloadEnd, onDownloadProgress, downloadUrlsMutation]);

  return { downloadSelectedPhotos, downloadState, clearDownloadOutcome };
};
